import { FormControlLabel, Radio, RadioGroup, Stack } from '@vapor/material'
import { ReservationF } from '../../../../types/reservation-detail.types'
import { useTranslation } from 'react-i18next'
import { FormItem, NumberField } from 'hic-components'
import { useCallback, useMemo, useState } from 'react'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { BillCategoryType, SplitType, useSplitProductionAssignedMutation } from 'src/api/api.generated'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { HICLoading } from 'hic-components'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { SelectReservationDestinationBill } from './reservation-destination-bill-select'
import { ProductionAssignedReturn, UnionBill } from './production-assigned-actions.type'

type ProductionAssignedActionSplitProps = {
  title: string
  open: boolean
  onClose: () => void
  bills: UnionBill[]
  productionAssignedIds: string[]
  originBillCategory?: BillCategoryType
  getProductionAssigned: (bills: UnionBill[], productionAssignedId: string) => ProductionAssignedReturn
  reservationId: string[]
  reservedResourceId?: string[]
  reservation?: ReservationF
}

export function ProductionAssignedActionSplit({
  title,
  open,
  onClose,
  bills,
  productionAssignedIds,
  getProductionAssigned,
  reservationId,
  reservedResourceId,
  originBillCategory,
  reservation,
}: ProductionAssignedActionSplitProps) {
  const { t } = useTranslation()
  const { id: tenantId, currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()
  const [splitMutation] = useSplitProductionAssignedMutation()
  const showError = useErrorSnackBar()
  const showSuccessSnackBar = useSuccessSnackBar()

  const [splitType, setSplitType] = useState<SplitType>('EQUAL_PARTS')
  const [splitTypeError, setSplitTypeError] = useState<string>()
  const [amountToDetach, setAmountToDetach] = useState<number>()
  const [amountToDetachError, setAmountToDetachError] = useState<string>()
  const [slices, setSlices] = useState<number>(2)
  const [slicesError, setSlicesError] = useState<string>()
  const [destinationBillId, setDestinationBillId] = useState<string | null>()

  const [loading, setLoading] = useState<boolean>(false)

  const uniqueProductionAssignedAmount = useMemo(() => {
    if (productionAssignedIds.length === 1) {
      const pa = getProductionAssigned(bills, productionAssignedIds[0])
      if (pa) return pa.production.amount * pa.quota * pa.production.quantity
    }
  }, [bills, productionAssignedIds, getProductionAssigned])

  const minDetachableAmount = useMemo(() => {
    if (uniqueProductionAssignedAmount && uniqueProductionAssignedAmount > 1) return 1
    else return 1
  }, [uniqueProductionAssignedAmount])

  const maxDetachableAmount = useMemo(() => {
    if (uniqueProductionAssignedAmount) {
      return uniqueProductionAssignedAmount - 1
    }
  }, [uniqueProductionAssignedAmount])

  const setDetachedValue = useCallback(
    (value: number | null) => {
      if (value) {
        if (value < minDetachableAmount) setAmountToDetachError(t('pms.booking.reservation.productions_assigned.action.invalid_amount_min', { min: formatCurrency(minDetachableAmount, currency) }))
        else if (maxDetachableAmount && value > maxDetachableAmount)
          setAmountToDetachError(t('pms.booking.reservation.productions_assigned.action.invalid_amount_max', { max: formatCurrency(maxDetachableAmount, currency) }))
        else {
          setAmountToDetachError(undefined)
          setAmountToDetach(value)
        }
      }
    },
    [minDetachableAmount, maxDetachableAmount, currency],
  )

  const validate = useCallback(() => {
    if (!splitType) {
      setSplitTypeError(t('pms.booking.reservation.productions_assigned.action.no_slit_type'))
    } else {
      switch (splitType) {
        case 'BY_VALUE':
          if (!amountToDetach) {
            setAmountToDetachError(t('pms.booking.reservation.productions_assigned.action.no_detach_amount'))
          } else {
            setAmountToDetachError(undefined)
            return true
          }
          break

        case 'EQUAL_PARTS':
          if (!slices || slices < 2 || slices > 10) {
            setSlicesError(t('pms.booking.reservation.productions_assigned.action.no_slices'))
          } else {
            setSlicesError(undefined)
            return true
          }
          break
      }
    }
    return false
  }, [splitType, amountToDetach, slices])

  const doAction = useCallback(async () => {
    if (validate()) {
      if (splitType) {
        setLoading(true)
        const result = await splitMutation({
          variables: {
            input: {
              tenantId: tenantId,
              productionAssigneds: productionAssignedIds,
              splitType: splitType,
              value: splitType === 'EQUAL_PARTS' ? slices ?? 2 : amountToDetach ?? 1,
              destinationBills: splitType === 'EQUAL_PARTS' && slices > 2 ? undefined : destinationBillId ? [destinationBillId] : undefined,
            },
          },
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onError() {},
          refetchQueries: ['ReservationDetail'],
        })
        setLoading(false)
        if (result.errors) {
          showError(result.errors)
        } else {
          showSuccessSnackBar(t('pms.booking.passerby-bill.creation-dialog.update_success'))
          onClose()
        }
      }
    }
  }, [productionAssignedIds, splitType, slices, amountToDetach, validate, destinationBillId])

  return (
    <FormLayout //
      title={title}
      appearance={{ type: 'DIALOG', open: open, onClose: onClose }}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={{
            clickHandler: () => {
              doAction()
            },
            text: t('commons.ok'),
          }}
          cancelButton={{ clickHandler: onClose }}
        />
      }
    >
      {loading && <HICLoading />}

      <Stack minWidth={300} minHeight={200} spacing={1}>
        <FormItem fieldLabel={t('pms.booking.reservation.productions_assigned.action.split.option.label')} errorMessage={splitTypeError}>
          <RadioGroup
            defaultValue={'EQUAL_PARTS'}
            onChange={(e) => {
              setSplitType(e.target.value as SplitType)
              setSplitTypeError(undefined)
            }}
          >
            <FormControlLabel value={SplitType.EQUAL_PARTS} control={<Radio />} label={t('pms.booking.reservation.productions_assigned.action.split.option')} />
            <FormControlLabel
              value={SplitType.BY_VALUE}
              control={<Radio />}
              label={t('pms.booking.reservation.productions_assigned.action.detach.option')}
              disabled={productionAssignedIds.length > 1}
            />
          </RadioGroup>
        </FormItem>
        {splitType === 'EQUAL_PARTS' && (
          <FormItem fieldLabel={t('pms.booking.reservation.productions_assigned.action.split_equal_parts')} errorMessage={slicesError}>
            <NumberField value={slices} onChange={(e) => setSlices(e?.valueOf() ?? 2)} decimals={0} nonNegative type="number" InputProps={{ inputProps: { min: 2, max: 10 } }} />
          </FormItem>
        )}
        {splitType === 'BY_VALUE' && (
          <FormItem fieldLabel={t('pms.booking.reservation.productions_assigned.action.split_by_value')} errorMessage={amountToDetachError}>
            <>
              <HicAmountField currency={currency} onChange={(x) => setDetachedValue(x)} InputProps={{ inputProps: { min: 0, max: maxDetachableAmount } }} />
            </>
          </FormItem>
        )}
        <FormItem
          fieldLabel={t('pms.booking.reservation.productions_assigned.action.destination_bill_label')}
          fieldTooltip={t('pms.booking.reservation.productions_assigned.action.destination_bill_label_hint')}
          disabled={!splitType || (slices && slices > 2) ? true : false}
        >
          <SelectReservationDestinationBill
            bills={bills}
            setDestinationBillId={(billId) => {
              setDestinationBillId(billId)
            }}
            reservationId={reservationId}
            reservedResourceId={reservedResourceId}
            defaultNewBillCategory={originBillCategory}
            reservation={reservation}
          />
        </FormItem>
      </Stack>
    </FormLayout>
  )
}
